// @flow
import * as React from 'react';
import { slide as Menu } from 'react-burger-menu';
import cn from 'classnames';
import qs from 'store/entities/QueryString';

// Components
import SidebarContext from '../SidebarContext';
import { Icon } from 'components/atoms/Icon/';
import Modal from 'components/atoms/Modal';
import Notifications from 'components/pages/User/Notifications';
import TermsPrivacy from 'components/pages/components/ContentFooter/components/TermsPrivacy';
import Profile from 'components/pages/User/Profile';

// Types
import { gtmUserEventType } from 'types/gtm';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Constants
import { USER_VEHICLES_ROUTE, HOME_ROUTE } from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';

import './SidebarMenu.scss';

// Utils
import { captureFocusUtilities } from 'utils/adaConfigurations/ModalFocusPrision';
import { clickEvent } from 'utils/adaConfigurations/customEvents';

const { BACK } = NAVIGATION;

type Props = {
  currentPath: string,
  gtmUserEvent: gtmUserEventType,
  isLoggedIn: boolean,
  phonePrivacyPreference: string,
  textPrivacyPreference: string,
  emailPrivacyPreference: string,
  emailAddress: Object,
  goTo: (path: string, direction: string) => void,
  isLoggedIn: boolean,
  getCountryStates: () => void,
  intl: IntlShape,
  getPhoneNumber: string => string,
  hideProfile: boolean
};

class SidebarMenu extends React.Component<Props> {
  static block = 'sidebar-menu';

  constructor(props: Props) {
    super(props);
    (this: any).notificationsModal = React.createRef();
    (this: any).menuContainer = React.createRef();
    (this: any).gtmSubMenu = this.gtmSubMenu.bind(this);
    (this: any).drawItemMenu = this.drawItemMenu.bind(this);
    (this: any).toggleNotificationsModal = this.toggleNotificationsModal.bind(
      this
    );
    (this: any).modal = React.createRef();
    (this: any).showHideProfileModal = this.showHideProfileModal.bind(this);
    (this: any).captureUtilities = captureFocusUtilities();
  }

  componentDidMount() {
    (this: any).captureUtilities.capture((this: any).menuContainer.current);
  }

  componentWillUnmount() {
    (this: any).captureUtilities.free();
  }

  notificationsModal: React.Ref<any>;
  menuContainer: React.Ref<any>;

  gtmSubMenu = (submenu: string) => {
    const { gtmUserEvent } = this.props;
    if (submenu !== 'Sign Out') {
      gtmUserEvent(
        `${submenu} Sub-menu Button`,
        `Clicked`,
        `Open ${submenu} Sub-menu`,
        `Settings Menu > ${submenu} Sub-menu`
      );
    }
  };

  modal: React.Ref<any>;

  showHideProfileModal = (show: boolean = true) => {
    const { getCountryStates } = this.props;
    getCountryStates();
    (this: any).modal.current.setShowModal(show);
  };

  getProfileModal = () => {
    const { isLoggedIn, emailAddress, getPhoneNumber } = this.props;
    const profileKey = `${
      emailAddress ? emailAddress.email : ''
    },${getPhoneNumber('MOBILE')}`;
    const nsModal = 'modal';
    return (
      <Modal className={`${nsModal}--backdrop`} ref={this.modal}>
        {isLoggedIn && (
          <Profile
            close={() => {
              this.showHideProfileModal(false);
            }}
            key={profileKey}
          />
        )}
      </Modal>
    );
  };

  drawItemMenu(
    itemName: string,
    icon: string,
    isActive?: boolean,
    onClickAction?: () => void,
    ariaLabel: string
  ) {
    const ns = SidebarMenu.block;
    const action = () => {
      this.gtmSubMenu(ariaLabel);
      onClickAction && onClickAction();
    };
    const itemClassName = cn(`${ns}__item`, {
      [`${ns}__item--active`]: isActive
    });
    const isTerms = icon === 'terms';
    const isPrivacy = icon === 'privacy';
    return (
      <div
        className={`${itemClassName} ${icon}`}
        onClick={() => action()}
        onKeyUp={clickEvent}
      >
        <Icon type={icon} className={`${ns}__item__icon`} aria-hidden={true} />
        {isTerms || isPrivacy ? (
          <TermsPrivacy
            className={`${ns}__item__item-name`}
            contentKey={`c8.${isTerms ? 'review.book' : 'privacy'}.disclaimer`}
            titleKey={`c8.sidebar.menu.${
              isTerms ? 'terms.conditions' : 'privacy.policy'
            }`}
            isTerms={isTerms}
          />
        ) : (
          <span
            className={`${ns}__item__item-name`}
            aria-label={ariaLabel}
            tabIndex={0}
          >
            {itemName}
          </span>
        )}
      </div>
    );
  }

  toggleNotificationsModal(show: boolean) {
    (this: any).notificationsModal.current.setShowModal(show);
  }

  notificationsAction(
    isVehicleActive: boolean,
    toggleSidebar: (isOpen: boolean) => void
  ) {
    const { goTo } = this.props;
    if (!isVehicleActive) {
      goTo(USER_VEHICLES_ROUTE, BACK);
    }
    toggleSidebar(false);
  }

  signOutAction(toggleSidebar: (isOpen: boolean) => void) {
    const { goTo, gtmUserEvent } = this.props;
    goTo(HOME_ROUTE, BACK);
    toggleSidebar(false);
    gtmUserEvent(
      'Sign Out Button',
      'Clicked',
      'Sign Out',
      'Settings Menu > Start Page'
    );
  }

  render() {
    const ns = SidebarMenu.block;
    const modalView = qs.get('vmode') === 'modal';
    const classProps = cn(ns, {
      [`c8-on-modal`]: modalView
    });
    const {
      phonePrivacyPreference,
      textPrivacyPreference,
      emailPrivacyPreference,
      emailAddress,
      intl,
      isLoggedIn,
      hideProfile
    } = this.props;
    const notificationsKey = `${phonePrivacyPreference},${textPrivacyPreference},${emailPrivacyPreference},${
      emailAddress ? emailAddress.email : ''
    }`;
    return (
      isLoggedIn && (
        <SidebarContext.Consumer>
          {({ isSidebarOpen, toggleSidebar }) => {
            const { currentPath, isLoggedIn } = this.props;
            const isVehicleActive = currentPath === USER_VEHICLES_ROUTE;
            return (
              <Menu
                right
                width={350}
                isOpen={isSidebarOpen}
                className={classProps}
                customBurgerIcon={false}
                customCrossIcon={false}
                onStateChange={state => toggleSidebar(state.isOpen)}
              >
                <div ref={this.menuContainer}>
                  <div className={`${ns}__header`}>
                    <div
                      className={`${ns}__close-icon-container`}
                      onKeyUp={clickEvent}
                      onClick={e => {
                        toggleSidebar(false);
                      }}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.close.profile.panel',
                        defaultMessage: 'Close User Profile Panel'
                      })}
                    >
                      <Icon
                        type={'close'}
                        aria-label={intl.formatMessage({
                          id: 'c8.aria.close',
                          defaultMessage: 'Close'
                        })}
                      />
                    </div>
                  </div>
                  {this.getProfileModal()}
                  <Modal
                    size={'large'}
                    ref={this.notificationsModal}
                    title={() => (
                      <h1 className={`${ns}__aria-main-title`}>
                        {intl.formatMessage({
                          id: 'c8.sidebar.menu.notifications'
                        })}
                      </h1>
                    )}
                  >
                    {isLoggedIn && (
                      <Notifications
                        closeModal={this.toggleNotificationsModal}
                        key={notificationsKey}
                      />
                    )}
                  </Modal>
                  <div className={`${ns}__section`}>
                    {this.drawItemMenu(
                      intl.formatMessage({
                        id: 'c8.sidebar.menu.my.vehicles'
                      }),
                      'vehicle',
                      isVehicleActive,
                      () =>
                        this.notificationsAction(
                          isVehicleActive,
                          toggleSidebar
                        ),
                      intl.formatMessage({
                        id: 'c8.aria.my.vehicles',
                        defaultMessage: 'My Vehicles'
                      })
                    )}
                    {!hideProfile &&
                      this.drawItemMenu(
                        intl.formatMessage({
                          id: 'c8.sidebar.menu.profile'
                        }),
                        'account',
                        false,
                        this.showHideProfileModal,
                        intl.formatMessage({
                          id: 'c8.aria.profile',
                          defaultMessage: 'User Profile'
                        })
                      )}
                    {this.drawItemMenu(
                      intl.formatMessage({
                        id: 'c8.sidebar.menu.notifications'
                      }),
                      'notifications',
                      false,
                      () => this.toggleNotificationsModal(true),
                      intl.formatMessage({
                        id: 'c8.aria.notifications',
                        defaultMessage: 'Notifications Preferences'
                      })
                    )}
                  </div>
                  <div className={`${ns}__section`}>
                    {this.drawItemMenu(
                      intl.formatMessage({
                        id: 'c8.sidebar.menu.terms.conditions'
                      }),
                      'terms',
                      false,
                      () => {},
                      intl.formatMessage({
                        id: 'c8.aria.terms.and.conditions',
                        defaultMessage: 'Terms and Conditions'
                      })
                    )}
                    {this.drawItemMenu(
                      intl.formatMessage({
                        id: 'c8.sidebar.menu.privacy.policy'
                      }),
                      'privacy',
                      false,
                      () => {},
                      intl.formatMessage({
                        id: 'c8.aria.privacy.policy',
                        defaultMessage: 'Privacy Policy'
                      })
                    )}
                  </div>

                  <div className={`${ns}__section border-bottom-0`}>
                    {this.drawItemMenu(
                      intl.formatMessage({
                        id: 'c8.sidebar.menu.sign.out'
                      }),
                      'signout',
                      false,
                      () => this.signOutAction(toggleSidebar),
                      intl.formatMessage({
                        id: 'c8.aria.sign.out',
                        defaultMessage: 'Sign Out'
                      })
                    )}
                  </div>
                </div>
              </Menu>
            );
          }}
        </SidebarContext.Consumer>
      )
    );
  }
}

export default SidebarMenu;



// WEBPACK FOOTER //
// ./src/components/pages/User/Sidebar/SidebarMenu/SidebarMenu.js